import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const RadioLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SwitchLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const SwitchCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

export const MIN_LOOKBACK_DAYS = 1;
export const MAX_LOOKBACK_DAYS = 365;

// CLE-2524 applicability choices for ladder rule types.
const APPLICABILITY_OPTIONS: {
  value: RulesetApplicability;
  label: string;
  help: string;
}[] = [
  {
    value: "SCOPED",
    label: "Payer-Provider rule",
    help: "Applies only to the selected provider and payer.",
  },
  {
    value: "TYPICAL",
    label: "Typical rule",
    help: "Template only. No effect until copied into a contract.",
  },
  {
    value: "FALLBACK",
    label: "Fallback rule",
    help: "Live — applies to every provider that has no more specific rule of this type. Optionally limit it to a single payer.",
  },
];

export interface IGlobalSurgicalPeriodFormData {
  groupName: string;
  providerId: string | null;
  payerId: number | null;
  applicability: RulesetApplicability;
  lookbackDays: number;
  requireGlobalRemark: boolean;
  allowPostopMap: boolean;
  contractLanguage: string;
}

export const INITIAL_GLOBAL_SURGICAL_PERIOD_FORM_DATA: IGlobalSurgicalPeriodFormData =
  {
    groupName: "",
    providerId: null,
    payerId: null,
    applicability: "SCOPED",
    lookbackDays: 90,
    requireGlobalRemark: false,
    allowPostopMap: true,
    contractLanguage: "",
  };

interface IGlobalSurgicalPeriodFormProps {
  title: string;
  submitLabel: string;
  formData: IGlobalSurgicalPeriodFormData;
  onChange: (data: IGlobalSurgicalPeriodFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** CLE-2524: applicability is immutable after create — edit mode locks the radio group. */
  scopeLocked?: boolean;
}

export function GlobalSurgicalPeriodForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: IGlobalSurgicalPeriodFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    const base = (payers?.data ?? []).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
    return [{ label: "All payers", value: null }, ...base];
  }, [payers]);

  // CLE-2597 — keep the scope fields consistent with the chosen applicability, so a
  // provider left over from SCOPED can't be submitted (and silently dropped) as a fallback.
  const handleApplicabilityChange = (value: RulesetApplicability) => {
    onChange({
      ...formData,
      applicability: value,
      providerId: value === "SCOPED" ? formData.providerId : null,
      payerId: value === "TYPICAL" ? null : formData.payerId,
    });
  };

  const handleChange = <K extends keyof IGlobalSurgicalPeriodFormData>(
    field: K,
    value: IGlobalSurgicalPeriodFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const lookbackIsValid =
    Number.isInteger(formData.lookbackDays) &&
    formData.lookbackDays >= MIN_LOOKBACK_DAYS &&
    formData.lookbackDays <= MAX_LOOKBACK_DAYS;

  const isValid =
    lookbackIsValid &&
    (formData.applicability !== "SCOPED" || formData.providerId !== null) &&
    !isSubmitting;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        {/* The one thing about this rule type nobody guesses from the form. */}
        <Message
          className="mb-4 w-full justify-content-start"
          severity="info"
          text="This rule applies to claims only. It finds the patient's prior surgery in their claim history, which an estimate does not have, so changing it never changes an estimate."
        />

        <FieldGrid>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional label shown in pricing breakdowns"
            />
          </Field>
          <Field>
            <label htmlFor="lookbackDays">Lookback (days)</label>
            <InputNumber
              inputId="lookbackDays"
              value={formData.lookbackDays}
              onValueChange={(e) => handleChange("lookbackDays", e.value ?? 90)}
              mode="decimal"
              useGrouping={false}
              min={MIN_LOOKBACK_DAYS}
              max={MAX_LOOKBACK_DAYS}
              // `inputClassName`, not `className`: the latter lands on InputNumber's wrapper,
              // where PrimeReact's invalid styling does not apply.
              inputClassName={lookbackIsValid ? undefined : "p-invalid"}
            />
            <FieldHelp>
              How far back to look for the index surgery, 1–365. The
              surgery&apos;s own CMS global period (10 or 90 days) still decides
              whether it covers the denied line, so a value above 90 cannot make
              the rule fire more often.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <RadioGroup>
          {APPLICABILITY_OPTIONS.map((option) => (
            <RadioLine key={option.value}>
              <RadioButton
                inputId={`applicability-${option.value}`}
                name="applicability"
                value={option.value}
                checked={formData.applicability === option.value}
                disabled={scopeLocked}
                onChange={() => handleApplicabilityChange(option.value)}
              />
              <label
                htmlFor={`applicability-${option.value}`}
                style={{ fontWeight: 600, cursor: "pointer" }}
              >
                {option.label}
              </label>
              <FieldHelp>{option.help}</FieldHelp>
            </RadioLine>
          ))}
        </RadioGroup>

        {formData.applicability === "SCOPED" && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerId}
                options={providerOptions}
                onChange={(e) => handleChange("providerId", e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; to apply these terms to every
                payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        {/* CLE-2597 — a fallback may name one payer: the same floor, every provider. */}
        {formData.applicability === "FALLBACK" && (
          <FieldGrid>
            <Field>
              <label htmlFor="fallbackPayerId">Payer</label>
              <Dropdown
                inputId="fallbackPayerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="All payers"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; for the global fallback. Pick a
                payer to set these terms for that payer across every provider —
                a rule written for a specific provider still wins over this.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        <SwitchLine>
          <InputSwitch
            inputId="requireGlobalRemark"
            checked={formData.requireGlobalRemark}
            onChange={(e) => handleChange("requireGlobalRemark", !!e.value)}
          />
          <SwitchCopy>
            <label
              htmlFor="requireGlobalRemark"
              style={{ fontWeight: 600, cursor: "pointer" }}
            >
              Require a global-period remark
            </label>
            <FieldHelp>
              On: only a line the payer marked N525 or M144 qualifies. Off (the
              default): a line denied with a clean CO-97 or CO-B15 also
              qualifies. Turn it on for a payer whose CO-97s mean something
              other than a global package.
            </FieldHelp>
          </SwitchCopy>
        </SwitchLine>

        <SwitchLine>
          <InputSwitch
            inputId="allowPostopMap"
            checked={formData.allowPostopMap}
            onChange={(e) => handleChange("allowPostopMap", !!e.value)}
          />
          <SwitchCopy>
            <label
              htmlFor="allowPostopMap"
              style={{ fontWeight: 600, cursor: "pointer" }}
            >
              Use the post-op procedure map
            </label>
            <FieldHelp>
              On (the default): the explicit list of post-op procedures pairs a
              surgery with its follow-up even when the two sit in different CPT
              chapters. Off: the surgery and the denied line must share a CPT
              body-system chapter.
            </FieldHelp>
          </SwitchCopy>
        </SwitchLine>

        <Field>
          <label htmlFor="contractLanguage">Contract language</label>
          <InputTextarea
            id="contractLanguage"
            value={formData.contractLanguage}
            onChange={(e) => handleChange("contractLanguage", e.target.value)}
            rows={6}
            autoResize
            maxLength={10000}
            placeholder="Shown on the claim when this rule applies"
          />
          <FieldHelp>
            Shown in the claim details modal beside every claim this rule
            priced.
          </FieldHelp>
        </Field>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
